import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useDocumentTemplates,
  useDeleteTemplate,
  useDuplicateTemplate,
  type TemplateCategory,
  type DocumentTemplate,
} from './hooks/useDocumentTemplates';
import { usePracticeEntities, type PracticeEntity } from '@/features/practice-entities';
import { REPORT_DATA_SOURCES } from './lib/reportPlaceholders';

/** Which family of templates the user is viewing. */
type TemplateType = 'billing' | 'report';

// Billing-family categories (everything that is not a report).
const BILLING_CATEGORIES: { value: TemplateCategory | ''; label: string }[] = [
  { value: '', label: 'All Categories' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'debit_note', label: 'Debit Note' },
  { value: 'quote', label: 'Quote' },
  { value: 'statement', label: 'Statement' },
  { value: 'letter', label: 'Letter' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'write_off', label: 'Write-Off' },
  { value: 'advance', label: 'Advance' },
];

function reportSourceLabel(value: string | null): string {
  if (!value) return '—';
  return REPORT_DATA_SOURCES.find((s) => s.value === value)?.label || value;
}

export function DocumentTemplatesPage() {
  const navigate = useNavigate();
  // null = show the "Billing vs Report" chooser first.
  const [templateType, setTemplateType] = useState<TemplateType | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | ''>('');
  const [entityFilter, setEntityFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState('');
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const isReportView = templateType === 'report';

  const { data: entities = [] } = usePracticeEntities({ isActive: true });

  // Report view: always filter to category='report'. Billing view: use the
  // selected billing category (or all billing categories when none selected).
  const { data: allTemplates, isLoading } = useDocumentTemplates({
    category: isReportView ? 'report' : categoryFilter || undefined,
    practice_entity_id: entityFilter || undefined,
    search: searchFilter || undefined,
  });

  // In billing view with no explicit category, exclude report templates.
  const templates = useMemo(() => {
    if (!allTemplates) return allTemplates;
    if (isReportView) return allTemplates;
    if (categoryFilter) return allTemplates;
    return allTemplates.filter((t) => t.category !== 'report');
  }, [allTemplates, isReportView, categoryFilter]);

  const deleteMutation = useDeleteTemplate();
  const duplicateMutation = useDuplicateTemplate();

  const handleDelete = (template: DocumentTemplate) => {
    if (window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      deleteMutation.mutate(template.id);
    }
  };

  const handleDuplicate = (id: string) => {
    if (!duplicateName.trim()) return;
    duplicateMutation.mutate(
      { id, name: duplicateName },
      {
        onSuccess: () => {
          setDuplicatingId(null);
          setDuplicateName('');
        },
      },
    );
  };

  // Step 0: choose which family of templates to manage.
  if (templateType === null) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Document Templates
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Choose which kind of templates you want to manage.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <button
            onClick={() => setTemplateType('billing')}
            className="flex flex-col items-start rounded-lg border border-gray-200 p-5 text-left transition hover:border-indigo-500 hover:shadow-sm dark:border-gray-700 dark:hover:border-indigo-400"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z"
                />
              </svg>
            </span>
            <span className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              Billing Templates
            </span>
            <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Invoices, quotes, credit notes, statements, receipts, letters, and more.
            </span>
          </button>

          <button
            onClick={() => setTemplateType('report')}
            className="flex flex-col items-start rounded-lg border border-gray-200 p-5 text-left transition hover:border-indigo-500 hover:shadow-sm dark:border-gray-700 dark:hover:border-indigo-400"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h12M3.75 3h-.75m.75 0h16.5m0 0h.75m-.75 0v11.25A2.25 2.25 0 0 1 18 16.5m0 0V21m-6-5.25V21M9 12l2.25 2.25L15 9"
                />
              </svg>
            </span>
            <span className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              Report Templates
            </span>
            <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Printable layouts for saved reports, built from their data fields.
            </span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => {
              setTemplateType(null);
              setCategoryFilter('');
              setEntityFilter('');
              setSearchFilter('');
            }}
            className="mb-1 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
            All Template Types
          </button>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {isReportView ? 'Report Templates' : 'Billing Templates'}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isReportView
              ? 'Design and manage printable layouts for your saved reports.'
              : 'Design and manage templates for invoices, quotes, statements, and more.'}
          </p>
        </div>
        <button
          onClick={() =>
            navigate(`/settings/document-templates/new?type=${isReportView ? 'report' : 'billing'}`)
          }
          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <svg
            className="-ml-0.5 mr-1.5 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {!isReportView && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as TemplateCategory | '')}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            aria-label="Filter by category"
          >
            {BILLING_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        )}

        <select
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          aria-label="Filter by practice entity"
        >
          <option value="">All Entities</option>
          {entities.map((entity: PracticeEntity) => (
            <option key={entity.id} value={entity.id}>
              {entity.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search templates..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white sm:w-64"
          aria-label="Search templates"
        />
      </div>

      {/* Templates List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
        </div>
      ) : templates && templates.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  {isReportView ? 'Report Category' : 'Category'}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Entity
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Page
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Default
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Version
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Updated
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/settings/document-templates/${template.id}`)}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                    >
                      {template.name}
                    </button>
                    {template.description && (
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                        {template.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {isReportView
                        ? reportSourceLabel(template.report_category)
                        : template.category.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {template.practice_entity_id ? (
                      entities.find((e: PracticeEntity) => e.id === template.practice_entity_id)
                        ?.name || '—'
                    ) : (
                      <span className="text-gray-400 italic">All</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {template.page_size} / {template.orientation}
                  </td>
                  <td className="px-4 py-3">
                    {template.is_default && (
                      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    v{template.version}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {new Date(template.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/settings/document-templates/${template.id}`)}
                        className="text-sm text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                        title="Edit template"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setDuplicatingId(template.id);
                          setDuplicateName(`${template.name} (Copy)`);
                        }}
                        className="text-sm text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                        title="Duplicate template"
                      >
                        Duplicate
                      </button>
                      <button
                        onClick={() => handleDelete(template)}
                        className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete template"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center dark:border-gray-600">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No templates</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isReportView
              ? 'Get started by creating your first report template.'
              : 'Get started by creating your first billing template.'}
          </p>
          <button
            onClick={() =>
              navigate(
                `/settings/document-templates/new?type=${isReportView ? 'report' : 'billing'}`,
              )
            }
            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Create Template
          </button>
        </div>
      )}

      {/* Duplicate Modal */}
      {duplicatingId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-label="Duplicate template"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Duplicate Template
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Enter a name for the copy.
            </p>
            <input
              type="text"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              aria-label="Template name"
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setDuplicatingId(null);
                  setDuplicateName('');
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDuplicate(duplicatingId)}
                disabled={!duplicateName.trim() || duplicateMutation.isPending}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
